import styled from 'styled-components';
import { currencyCodeSuffix } from '../../utils/money';
import tw from 'twin.macro';
import _ from 'lodash';
import { IGetBookingQuery } from '../../services/bookingService/__generated__/queries.generated';
import { youngsterFeeDetail } from '../../utils/receiptFeeLabels';
const PriceBreakdown = styled.div`
  ${tw`
    border-t
    border-border-default
    mt-4
    pt-4
    w-full
    max-w-full
    min-w-0
    overflow-x-hidden
    box-border
  `}
`;

const PriceRow = styled.div<{ isOdd: boolean }>`
  ${tw`
    flex
    items-start
    justify-between
    mb-2
    p-2
    rounded-md
    min-w-0
    max-w-full
    gap-3
  `}
  ${props => (props.isOdd ? tw`bg-bg-secondary` : tw`bg-white`)}
`;

const PriceRowLabel = styled.div`
  ${tw`flex-1 min-w-0`}
`;

const PriceDetails = styled.div`
  ${tw`
    text-right
    flex-shrink-0
    whitespace-nowrap
    pl-1
  `}
`;

const PriceLabel = styled.span`
  ${tw`
    text-text-secondary
    break-words
  `}
`;

const TotalRow = styled(PriceRow)`
  ${tw`flex-col items-stretch gap-1`}
`;

const TotalRowMain = styled.div`
  ${tw`flex w-full items-start justify-between gap-3 min-w-0`}
`;

const SmallText = styled.span`
  ${tw`
    text-sm
    text-text-secondary
    mt-1
  `}
`;

const Value = styled.span`
  ${tw`
    font-medium
  `}
`;

const SecurityDepositNote = styled.span`
  ${tw`
    text-sm
    text-text-secondary
    mt-1
    block
  `}
`;

const DisCountAppliedLabel = styled.span`
  ${tw`
    ml-2
    text-sm
    text-text-secondary
    mt-1
    block
    bg-green-100
  `}
`;

const DisCountApplied = ({
  discountAmount,
  discountPercentage,
}: {
  discountAmount?: number;
  discountPercentage?: number;
}) => {
  return discountAmount && discountAmount > 0 ? (
    <DisCountAppliedLabel>
      <div>
        <PriceLabel>
          {discountPercentage && discountPercentage > 0
            ? ` ${discountPercentage}% off of ${discountAmount.toFixed(2)} Applied`
            : ''}
        </PriceLabel>
      </div>
    </DisCountAppliedLabel>
  ) : null;
};

function ChargingDetails({
  receipt,
  currency = 'USD',
  taxLabel = 'Tax',
}: {
  receipt: IGetBookingQuery['booking']['receipt'];
  /** Spec 028: the trip's currency; CAD adds " CAD" after the total (D14). */
  currency?: string;
  /** Spec 028: "HST", "GST" or "Tax" (D16). */
  taxLabel?: string;
}) {
  const code = currencyCodeSuffix(currency);
  const getRenderingByKey = (
    key: string,
    value: number,
  ): { label: string; smallLabel: string; val: number } => {
    switch (key) {
      case 'tripFeeTotal':
        return {
          label: 'Trip Fee',
          smallLabel: `(${receipt?.totalDays} days @ ${
            Number(receipt?.tripFeeTotal || 0) / Number(receipt?.totalDays || 1)
          }/day)`,
          val: value,
        };
      case 'youngsterFeeTotal':
        return {
          label: 'Youngster Fee',
          smallLabel: youngsterFeeDetail(receipt ?? {}),
          val: value,
        };
      case 'carPricePerTrip':
        return {
          label: 'Rental Price',
          smallLabel: `(${receipt?.totalDays} days @ ${receipt?.pricePerDay}/day${receipt?.lastMinSurchargeAmount && receipt.lastMinSurchargeAmount > 0 ? `, incl. ${receipt.lastMinSurchargePercentage}% last-min surcharge` : ''})`,
          val: value,
        };
      case 'deliveryFee':
        return {
          label: 'Delivery Fee',
          smallLabel: '',
          val: value,
        };
      case 'insuranceByTrip':
        return {
          label: 'Protection plan',
          smallLabel: `(${receipt?.totalDays} days @ ${receipt?.insurancePerDay}/day)`,
          val: value,
        };

      case 'tax':
        return {
          label: taxLabel,
          smallLabel: `${' '}(${Number(receipt?.taxRate) * 100}%)`,
          val: value,
        };

      case 'securityDeposit':
        return {
          label: 'Security Deposit',
          smallLabel: 'Held until trip completion + 80 hours',
          val: value,
        };

      default:
        return {
          label: key
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, str => str.toUpperCase()),
          smallLabel: '',
          val: value,
        };
    }
  };
  return (
    <PriceBreakdown>
      {Object.entries(receipt || {}).map(([key, value], index) => {
        // Skip certain fields that we don't want to display
        if (['paymentMethod', 'last4digitOfCard', 'balance'].includes(key)) {
          return null;
        }

        // Special formatting for different types of values
        switch (key) {
          case 'tripFee':
            return null;
          case 'pricePerDay':
            return null;

          case 'insurancePerDay':
            return null;

          case 'pricePerDay':
            return null;

          case 'totalPrice':
            return null;

          case 'totalPriceWithFees':
            // Skip totalPriceWithFees as it's the same as totalPrice
            return null;

          case 'totalDays':
            return null;

          case 'subtotal':
            return null;

          case 'taxRate':
            return null;

          case 'youngsterFee':
            return null;

          case 'securityDeposit':
            // Skip security deposit as a separate row, will show as note below Total
            return null;

          case 'couponDiscount':
            // Skip couponDiscount in the loop, we'll display it separately before Total
            return null;

          case 'couponCode':
            // Skip couponCode in the loop, we'll display it with couponDiscount
            return null;
          case 'discountAmount':
            // Skip discountAmount in the loop, we'll display it after pricing rows
            return null;
          case 'discountPercentage':
            // Skip discountPercentage in the loop, we'll display it after discountAmount
            return null;
          case 'lastMinSurchargePercentage':
          case 'lastMinSurchargeAmount':
            // Skip surcharge fields in the loop, displayed inline after Rental Price
            return null;

          case 'allowedMileage':
            // Mileage is displayed above the terms section, not in the price receipt
            return null;

          case 'monthlyTripFeeRate':
          case 'monthlyYoungsterFeeRate':
            // Rates are already factored into Trip Fee / Youngster Fee totals above
            return null;

          case 'rentalType':
            return null;

          default:
            if (typeof value === 'number' && value > 0) {
              const { label, smallLabel, val } = getRenderingByKey(key, value);
              return (
                <>
                  <PriceRow key={key} isOdd={index % 2 === 0}>
                    <PriceRowLabel>
                      <PriceLabel>{label}</PriceLabel>
                      <SmallText>{smallLabel}</SmallText>
                    </PriceRowLabel>
                    <PriceDetails>
                      <Value>${val.toFixed(2)}</Value>
                    </PriceDetails>
                  </PriceRow>
                  {label === 'Rental Price' ? (
                    <DisCountApplied
                      discountAmount={receipt?.discountAmount ?? undefined}
                      discountPercentage={receipt?.discountPercentage ?? undefined}
                    />
                  ) : null}
                </>
              );
            }
            return null;
        }
      })}

      {/* Coupon Discount - Display before Total */}
      {receipt?.couponDiscount && receipt.couponDiscount > 0 ? (
        <PriceRow isOdd={false}>
          <PriceRowLabel>
            <PriceLabel>
              Coupon Discount {receipt.couponCode ? `(${receipt.couponCode})` : ''}
            </PriceLabel>
          </PriceRowLabel>
          <PriceDetails>
            <Value style={{ color: '#10b981', fontWeight: '600' }}>
              -${receipt.couponDiscount.toFixed(2)}
            </Value>
          </PriceDetails>
        </PriceRow>
      ) : null}

      <TotalRow isOdd>
        <TotalRowMain>
          <PriceLabel>Total</PriceLabel>
          <PriceDetails>
            <Value>
              ${receipt?.totalPrice?.toFixed(2)}
              {code}
            </Value>
          </PriceDetails>
        </TotalRowMain>
        {receipt?.securityDeposit && receipt.securityDeposit > 0 ? (
          <SecurityDepositNote>
            (Security Deposit: ${receipt.securityDeposit.toFixed(2)}
            {code} held until trip completion + 80 hours)
          </SecurityDepositNote>
        ) : null}
      </TotalRow>
    </PriceBreakdown>
  );
}

export default ChargingDetails;
